/**
 * Abrechnung (Projekt-Sichten Phase 2, 27.08.2026): eigene Unterseite je Auftrag, ausgelagert
 * aus der bisherigen L3-Karte auf event-detail.tsx. Antoines Vorgabe: die Auftragsseite zeigt nur
 * noch eine kompakte Zusammenfassung (Gesamt/bezahlt/offen/nächste Fälligkeit) mit Link hierher —
 * alle Aktionen (Kostenübernahme, Rechnungen, Zahlungen) leben ab jetzt ausschließlich hier.
 * Inhalt/Verhalten 1:1 aus der ehemaligen Abrechnungs-Karte übernommen, nur der Ort hat sich
 * geändert.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useVarianteWahl } from "../ui/VarianteWaehlenDialog";
import { BezahltDialog } from "../ui/BezahltDialog";
import { RechnungSendenDialog } from "../ui/RechnungSendenDialog";
import { rechnungGesendetText, rechnungOffenGrund, rechnungSendbar } from "../lib/rechnung-offen";
import { AbrechnenReiter } from "../ui/abrechnung/AbrechnenReiter";
import { useApi, apiMutate } from "../hooks/useApi";
import { abrechnungPfad, angebotsseitePfad, vorgangPfad, zahlungenPfad } from "../lib/pfade";
import { useSeitenKontextMelden, vorgangsListe } from "../lib/rueckweg";
import type { ApiError } from "../lib/api";
import type { Customer, EventRow, Invoice, Payment } from "../ui/types";
import { anlassAnzeige, EVENT_STATUS, INVOICE_TYPE_LABEL, PAYMENT_STATUS, PAYMENT_TYPE_LABEL, PILL_CLASSES, formatDate, money, parseGermanEurosToCents } from "../ui/format";
import { KostenuebernahmeDetail, NOT_REQUESTED, type CostAcceptance } from "../ui/auftrag/KostenuebernahmeDetail";
import { AbrechnungDokumente } from "../ui/auftrag/AbrechnungDokumente";
import { useKueVersionen, versionsName } from "../ui/auftrag/KostenuebernahmeVersionen";
import { AENDERUNGS_ANLASS_TEXT } from "../lib/activity-labels";
import { AutomatischErkanntChip, KUNDENTYP_TEXT, KundentypAuswahl, type Kundentyp } from "../ui/Kundentyp";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, PageHeader, Select, StatusPill, TabBar, Zeichenzaehler, useBestaetigung, DateInput, Checkbox, Textarea, useToastWennVorhanden } from "../ui/components";
import { TextbausteinPicker, insertAtCursor } from "../ui/TextbausteinPicker";
import { rechnungsZustand, type ZustandsTon } from "../lib/rechnungs-zustand";
import { deckungsSatz, fehlbetragCents, type Deckung } from "../lib/zahlungsdeckung-anzeige";
import { pruefeStaffel, type StaffelEingabe } from "../lib/zahlungsstaffel-anzeige";
import { ZahlungsplanAnpassenDrawer } from "../ui/auftrag/ZahlungsplanAnpassenDrawer";
import { kennzahlenFuerVorgang, kostenuebernahmeNeuanforderungHinweis, nettoBezahltCents, schuldNachErstattungCents } from "../lib/zahlungs-kennzahlen";
import { tageBisEvent, ermittleKeepPercent, berechneEinbehalt, type CancellationTier } from "../lib/storno-vorschlag";

// A3 (13.09.2026): `CostAcceptance`, `caMeta`, `NOT_REQUESTED` und die gesamte Darstellung der
// Kostenuebernahme-Zeile leben seit Einheit A in `ui/auftrag/KostenuebernahmeDetail.tsx` — die
// Vorgangsseite zeigt exakt dieselbe Darstellung in einem Drawer, und zwei Abschriften desselben
// Blocks waeren die zweite Wahrheit gewesen, die Migration 357 hier schon einmal teuer gemacht hat.

// Die Antwortform `Invoice` und die Typ-Beschriftung (frueher `INV_TYPE_LABEL`) lagen bis zum
// Dokumente-Reiter (16.09.2026) hier lokal. Seit die Dokumente-Ansicht dieselben Belege ein
// zweites Mal auflistet, stehen beide in `ui/types.ts` bzw. `ui/format.ts` (`INVOICE_TYPE_LABEL`)
// — reines Verschieben, gleiche Felder, gleiche Texte.
// WARTESCHLANGE Punkt 6, Teil c (14.09.2026, Antoine wörtlich: "Mir ist auch nicht klar, was der
// Link 'Anzahlung' auslöst."): `createInvoice(type)` legt SOFORT eine echte, nummerierte
// LexOffice-Rechnung an (apps/api/src/lib/lexoffice-invoice.ts ruft LexOffice immer mit
// `?finalize=true` — kein Entwurf-dann-finalisieren-Zweistufer). Eine echte Vorschau ohne
// Beleganlage wäre eine neue LexOffice-Integrationsfläche (nicht im Umfang) — stattdessen klärt
// ein Bestätigungsdialog VOR dem Klick, was passiert, gleiches Muster wie `cancelInvoice` unten.
const INV_CONFIRM_TITEL: Record<"invoice" | "deposit" | "final", string> = {
  deposit: "Anzahlungsrechnung wirklich erstellen?",
  final: "Schlussrechnung wirklich erstellen?",
  invoice: "Rechnung wirklich erstellen?",
};
const INV_CONFIRM_TEXT =
  "Es wird sofort eine echte, nummerierte Rechnung bei LexOffice angelegt. Der Versand an den Kunden ist ein separater Schritt danach („An Kunden senden“).";
const TON_CLS: Record<ZustandsTon, string> = {
  neutral: PILL_CLASSES.neutral,
  warn: PILL_CLASSES.warn,
  success: PILL_CLASSES.success,
  danger: PILL_CLASSES.danger,
};
const invMeta = (inv: Pick<Invoice, "status" | "hasLexDocument" | "creationUncertain" | "invoiceType">) => {
  const z = rechnungsZustand(inv);
  return { ...z, cls: TON_CLS[z.ton] };
};

export default function AbrechnungPage() {
  const { id } = useParams<{ id: string }>();
  const eventQ = useApi<EventRow>(id ? `/api/events/${id}` : null);
  const event = eventQ.data;
  // Punkt 115 (G4): wie auf der Vorgangsseite — Liste markieren, Vorgangsname als Titel.
  useSeitenKontextMelden(event ? vorgangsListe(event).to : null, event ? (anlassAnzeige(event.occasion) ?? "Vorgang") : null);
  const customerQ = useApi<Customer>(event ? `/api/customers/${event.customerId}` : null);
  const paymentsQ = useApi<Payment[], { deckung?: Deckung }>(id ? `/api/events/${id}/payments` : null);
  const caQ = useApi<CostAcceptance | null>(id ? `/api/events/${id}/cost-acceptance` : null);
  const invoicesQ = useApi<Invoice[]>(id ? `/api/events/${id}/invoices` : null);
  // F3 (docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md): dieselbe Route wie event-detail.tsx —
  // KEIN Rollen-Gate, liefert `cancellationTiers` als Teil von TENANT_SETTINGS_SICHTBAR
  // (Migration 404). Gebraucht fuer den Rueckerstattungs-Vorschlag im Refund-Drawer unten UND
  // (Warteschlangenpunkt 34) fuer `mailFreigabeModus` im Bestaetigungstext von „Bezahlt
  // markieren" — die Abfrage laeuft ohnehin, es kommt keine zweite dazu.
  // WvonZ-Fund 20.10.2026: derselbe Lesepfad wie die Modul-Kachel (Einstellungen → Module) liefert
  // `lexofficeKeyHinterlegt`. `false` blendet die Rechnungs-Knöpfe aus (sonst 503
  // invoice_not_configured); `null`/lädt/Fehler lässt sie wie bisher stehen (kein Flackern).
  const moduleQ = useApi<{ lexofficeKeyHinterlegt?: boolean | null } | null>("/api/settings/module");
  const lexofficeKeyFehlt = moduleQ.data?.lexofficeKeyHinterlegt === false;
  const settingsQ = useApi<{
    cancellationTiers?: CancellationTier[] | null;
    mailFreigabeModus?: "manuell" | "automatisch";
  } | null>("/api/settings/payment-terms");

  /**
   * Reiter (Antoine, 16.09.2026, freigegebenes Mockup): „Übersicht" ist und bleibt der
   * Startzustand — die Seite zeigt beim Aufruf exakt das, was sie vorher zeigte. „Dokumente" ist
   * rein additiv; seine beiden zusaetzlichen Abfragen leben IN `AbrechnungDokumente` und laufen
   * damit erst, wenn der Reiter tatsaechlich offen ist.
   */
  // Abrechnung Stufe 1 (Mockup 28.09.2026): dritter Reiter „Abrechnen“; `?reiter=abrechnen` öffnet ihn
  // direkt (so springt „Zur Abrechnung“ ab dem Tag nach der Feier hinein).
  const [suche, setSuche] = useSearchParams();
  const reiterAusUrl = suche.get("reiter");
  const [tab, setTabRoh] = useState<"uebersicht" | "abrechnen" | "dokumente">(reiterAusUrl === "abrechnen" || reiterAusUrl === "dokumente" ? reiterAusUrl : "uebersicht");
  const setTab = (t: "uebersicht" | "abrechnen" | "dokumente") => {
    setTabRoh(t);
    const n = new URLSearchParams(suche);
    if (t === "uebersicht") n.delete("reiter");
    else n.set("reiter", t);
    setSuche(n, { replace: true });
  };
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  // 26.10.2026: mehrere aktive Varianten ohne Wahl -> Server fragt nach, hier der Dialog dazu.
  const { mitVarianteWahl, dialog: varianteDialog } = useVarianteWahl();
  const [bezahltZahlung, setBezahltZahlung] = useState<Payment | null>(null);
  // Einheit E (26.10.2026): „Rechnung senden“ von Hand zu einer bezahlten Zahlung ohne versendete Rechnung.
  const [rechnungZahlung, setRechnungZahlung] = useState<Payment | null>(null);
  // P3 (Punkt 29): `?erstatten=<paymentId>&betrag=<cents>` öffnet die bestehende Stripe-Erstattung
  // dieser Zahlung, vorbelegt — ausgelöst wird erst mit dem Klick im Dialog (kein Auto-Refund).
  useEffect(() => {
    const zielId = suche.get("erstatten");
    if (!zielId || !paymentsQ.data) return;
    const ziel = paymentsQ.data.find((p) => p.id === zielId && p.status === "succeeded" && p.stripePaymentIntentId);
    const n = new URLSearchParams(suche);
    n.delete("erstatten");
    n.delete("betrag");
    setSuche(n, { replace: true });
    if (!ziel) return;
    const betrag = Number(suche.get("betrag"));
    setActionError(null);
    setRefundGrund("");
    setSchuldRueckfrage(null);
    setSchuldBestaetigt(false);
    setRefundVorbelegtCents(Number.isInteger(betrag) && betrag > 0 ? Math.min(betrag, nettoBezahltCents(ziel)) : null);
    setRefundVorgangId(crypto.randomUUID());
    setRefundTarget(ziel);
  }, [suche, paymentsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  /*
   * Punkt 89: Betrag und Prozentsatz sind ZWEI SICHTEN AUF DIESELBE ZAHL. Beide im State, weil
   * jede Eingabe die andere nachzieht — gespeichert wird beim Absenden aber nur der Betrag.
   */
  const [zahlungBetrag, setZahlungBetrag] = useState("");
  const [zahlungProzent, setZahlungProzent] = useState("");
  // Stripe-Erstattung (2026-08-30, aufgaben.md "Offen aus 2f"): die Zahlung, fuer die der
  // Erstattungs-Dialog gerade offen ist (null = geschlossen). Betrag ist vorbefuellt/editierbar,
  // Server prueft ihn erneut gegen den tatsaechlich gezahlten Betrag (payments.ts, mode 'stripe').
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  // Punkt 110: eine ID je geoeffnetem Erstattungsdialog = ein Erstattungs-Vorgang (Stripe-Idempotenz).
  const [refundVorgangId, setRefundVorgangId] = useState("");
  // Punkt 93: Pflichtbegruendung der Erstattung — steht danach im Verlauf (Server prueft erneut).
  const [refundGrund, setRefundGrund] = useState("");
  // Punkt 93 (Antoine, 23.09.2026): Rueckfrage, wenn der Gast nach der Erstattung noch etwas
  // schuldet. Gilt nur fuer GENAU den Betrag, fuer den sie gestellt wurde.
  const [schuldRueckfrage, setSchuldRueckfrage] = useState<{ amountCents: number; schuldCents: number } | null>(null);
  const [schuldBestaetigt, setSchuldBestaetigt] = useState(false);
  // P3 (Punkt 29): Vorbelegung aus „Überschuss erstatten …“ (Vorgangsseite) — schlägt die übrigen Vorschläge.
  const [refundVorbelegtCents, setRefundVorbelegtCents] = useState<number | null>(null);
  const [staffelOpen, setStaffelOpen] = useState(false);
  const [staffelZeilen, setStaffelZeilen] = useState<StaffelEingabe[]>([]);
  const [anpassenOpen, setAnpassenOpen] = useState(false);
  const [caBusy, setCaBusy] = useState(false);
  // Vorschau (08.09.2026, Antoine, Auslöser: Kostenübernahme an Jad El Haber mit kaputtem Layout -
  // 9 statt 3 Seiten, erst nach dem Versand bemerkt): eigener Busy-Zustand, unabhängig von
  // `caBusy` (Senden) — beide Buttons sollen sich nicht gegenseitig sperren.
  const [caPreviewBusy, setCaPreviewBusy] = useState(false);
  const [caTest, setCaTest] = useState(false);
  const [caDialogOpen, setCaDialogOpen] = useState(false);
  const [caPersonalNote, setCaPersonalNote] = useState("");
  // A-la-carte (2026-09-03, Domenico/Storia): explizite Bestaetigung "kein fester Endbetrag" -
  // oeffnet den Kostenuebernahme-Versandweg fuer Vorgaenge ohne Preis (Betrag entsteht erst vor
  // Ort je nach Bestellung), ohne dass ein Platzhalter-Preis (z. B. 1 €) in die Angebotsvariante
  // eingetragen werden muss. Server: routes/cost-acceptance.ts, `noFixedAmount`.
  const [caOpenPricing, setCaOpenPricing] = useState(false);
  /*
   * Ziffer 13 des Vertrags, „Individuelle Vereinbarungen" (Fassung 2.0, 22.09.2026). Bis dahin
   * trug diese Ziffer die STORNOREGELN — dieselben, die schon in Ziffer 7 stehen; laut der
   * Rangfolge-Klausel des Vertrags sogar mit Vorrang vor Angebot und AGB. Was hier eingetippt
   * wird, gilt nur fuer DIESEN Vertrag; leer bleibt die Ziffer leer.
   */
  const [caVereinbarungen, setCaVereinbarungen] = useState("");
  const caNoteRef = useRef<HTMLTextAreaElement | null>(null);
  /*
   * Punkt 42 (Entscheidung 5): ist der Kundentyp nur automatisch erkannt, antwortet der Server beim
   * Senden mit 409 `kundentyp_unbestaetigt`. Dann fragt MAESTRO EINMAL hier im Dialog nach; die
   * Antwort geht mit dem nächsten „Senden“ mit und wird am Kunden gespeichert.
   */
  const [kundentypFrage, setKundentypFrage] = useState<Kundentyp | null>(null);
  const [kundentypWahl, setKundentypWahl] = useState<Kundentyp | null>(null);
  useEffect(() => {
    if (caDialogOpen) {
      setCaPersonalNote("");
      setCaOpenPricing(false);
      setCaVereinbarungen("");
      setKundentypFrage(null);
      setKundentypWahl(null);
    }
  }, [caDialogOpen]);
  const [invBusy, setInvBusy] = useState(false);
  const [invSendDialogFor, setInvSendDialogFor] = useState<string | null>(null);
  const [invPersonalNote, setInvPersonalNote] = useState("");
  const invNoteRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (invSendDialogFor) setInvPersonalNote("");
  }, [invSendDialogFor]);

  // KÜ V2 (E12): ersetzt die neue Kostenübernahme eine gültige, unterschriebene, fragt der Server
  // nach dem Anlass — sofern er nicht schon beim „Für Änderung öffnen“ gewählt wurde.
  const gueltigeKue = useKueVersionen(id ?? null).data?.find((v) => v.gilt) ?? null;
  const [caAnlass, setCaAnlass] = useState<"" | "gastwunsch" | "betriebsfehler" | "sonstiges">("");

  async function requestCostAcceptance(personalNote?: string, openPricing?: boolean, vereinbarungen?: string, kundentyp?: Kundentyp | null) {
    if (!id) return;
    setCaBusy(true);
    setActionError(null);
    try {
      const note = personalNote?.trim();
      await apiMutate(`/api/events/${id}/cost-acceptance`, "POST", {
        test: caTest,
        ...(note ? { personalNote: note } : {}),
        ...(openPricing ? { noFixedAmount: true } : {}),
        ...(vereinbarungen?.trim() ? { individuelleVereinbarungen: vereinbarungen.trim() } : {}),
        ...(kundentyp ? { kundentyp } : {}),
        ...(caAnlass && !caTest ? { anlass: caAnlass } : {}),
      });
      setCaDialogOpen(false);
      setCaPersonalNote("");
      setCaOpenPricing(false);
      caQ.reload();
    } catch (err) {
      const api = err as ApiError;
      if (api.code === "kundentyp_unbestaetigt") {
        const erkannt = (api.body as { erkannt?: Kundentyp } | undefined)?.erkannt ?? "firma";
        setKundentypFrage(erkannt);
        setKundentypWahl(erkannt);
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /no_template|Vorlage/i.test(msg)
          ? "eSign-Vorlage fehlt: unter Einstellungen → Module → E-Signatur die Template-ID hinterlegen."
          : /esign_not_configured|503/i.test(msg)
            ? "eSign ist nicht konfiguriert (COMPOSIO_API_KEY im Worker fehlt)."
            : /no_amount/i.test(msg)
              ? 'Kein positiver Vertragsbetrag vorhanden. Bitte zuerst die Angebotsvariante mit Betrag pflegen — oder unten "Kein fester Endbetrag (A-la-carte)" ankreuzen, wenn die Kosten erst vor Ort je nach Bestellung entstehen.'
              : msg,
      );
    } finally {
      setCaBusy(false);
    }
  }

  /**
   * Vorschau (08.09.2026, Antoine, Auslöser: Kostenübernahme an Jad El Haber mit kaputtem Layout -
   * 9 statt 3 Seiten, erst nach dem Versand bemerkt): erzeugt einen ECHTEN, vollständig
   * gerenderten eSignatures.io-Test-Contract (server: routes/cost-acceptance.ts, POST
   * .../cost-acceptance/vorschau) — OHNE dass der Kunde benachrichtigt wird und OHNE dass eine
   * Zeile in `cost_acceptances` entsteht. Öffnet die zurückgegebene Signatur-Seite in einem neuen
   * Tab, damit der Betreiber das fertige Dokument sieht, BEVOR er unten auf "Senden" klickt.
   */
  async function previewCostAcceptance(openPricing?: boolean, vereinbarungen?: string) {
    if (!id) return;
    setCaPreviewBusy(true);
    setActionError(null);
    try {
      const { previewUrl } = await apiMutate<{ previewUrl: string }>(`/api/events/${id}/cost-acceptance/vorschau`, "POST", {
        ...(openPricing ? { noFixedAmount: true } : {}),
        // Die Vorschau muss DENSELBEN Vertrag zeigen wie der Versand — sonst prueft man ein
        // anderes Dokument als das, was hinausgeht (genau der Jad-El-Haber-Fall).
        ...(vereinbarungen?.trim() ? { individuelleVereinbarungen: vereinbarungen.trim() } : {}),
      });
      window.open(previewUrl, "_blank", "noopener");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /no_template|Vorlage/i.test(msg)
          ? "eSign-Vorlage fehlt: unter Einstellungen → Module → E-Signatur die Template-ID hinterlegen."
          : /esign_preview_not_supported/i.test(msg)
            ? "Der eSign-Adapter unterstützt keine Vorschau."
            : /esign_not_configured|503/i.test(msg)
              ? "eSign ist nicht konfiguriert (COMPOSIO_API_KEY im Worker fehlt)."
              : /no_amount/i.test(msg)
                ? 'Kein positiver Vertragsbetrag vorhanden. Bitte zuerst die Angebotsvariante mit Betrag pflegen — oder unten "Kein fester Endbetrag (A-la-carte)" ankreuzen, wenn die Kosten erst vor Ort je nach Bestellung entstehen.'
                : msg,
      );
    } finally {
      setCaPreviewBusy(false);
    }
  }

  /**
   * Widerruf einer zugestellten (noch nicht signierten) Kostenübernahme — Gegenstück zu
   * `POST /api/events/:id/cost-acceptance/withdraw` (apps/api/src/routes/cost-acceptance.ts). Die
   * Route erlaubt den Widerruf NUR aus Status 'sent' (409 invalid_status sonst) — eine bereits
   * signierte Kostenübernahme ist verbindlich und über diesen Endpunkt bewusst NICHT widerrufbar.
   * Rechtlich relevante Aktion (entwertet den Signaturlink beim Anbieter sofort, unumkehrbar) ->
   * MAESTRO-Bestätigungsdialog mit klarem Wortlaut, gleiches Muster wie `cancelInvoice`/`paymentAction("paid")`
   * oben in dieser Datei. Fehler ehrlich anzeigen (kein stilles Scheitern) statt nur zu loggen.
   */
  async function withdrawCostAcceptance() {
    if (!id || !caQ.data) return;
    if (
      !(await bestaetigen({
        titel: "Kostenübernahme wirklich widerrufen?",
        text: "Der Signatur-Link wird beim eSign-Anbieter sofort entwertet und ist für den Kunden nicht mehr nutzbar. Diese Aktion kann nicht rückgängig gemacht werden — für einen neuen Vertrag muss die Kostenübernahme erneut angefordert werden.",
        bestaetigen: "Widerrufen",
        gefahr: true,
      }))
    )
      return;
    setCaBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/cost-acceptance/withdraw`, "POST", {});
      caQ.reload();
    } catch (err) {
      // Der Backend-Code (nicht der Freitext) entscheidet - toApiError haengt ihn als `code` an
      // (siehe anfrage-detail.tsx, gleiches Muster). Regex auf den lokalisierten Nachrichtentext
      // waere brueckig (der Text enthaelt den Codenamen nicht woertlich); alle Codes der Route
      // (cost-acceptance.ts /withdraw) liefern ohnehin bereits eine klare deutsche Meldung, die
      // unveraendert durchgereicht wird - nur 'esign_not_configured' bekommt hier einen Hinweis,
      // dass GENAU DESHALB kein Widerruf moeglich ist (sonst liesse der Text offen, was zu tun ist).
      const code = err instanceof Error && "code" in err ? (err as ApiError).code : undefined;
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(code === "esign_not_configured" ? `${msg} Widerruf nicht möglich.` : msg);
    } finally {
      setCaBusy(false);
    }
  }

  async function createInvoice(type: "invoice" | "deposit" | "final") {
    if (!id) return;
    if (!(await bestaetigen({ titel: INV_CONFIRM_TITEL[type], text: INV_CONFIRM_TEXT, bestaetigen: "Rechnung erstellen" }))) return;
    setInvBusy(true);
    setActionError(null);
    try {
      const neu = await apiMutate<{ hinweis?: string | null }>(`/api/events/${id}/invoice`, "POST", { type });
      invoicesQ.reload();
      // Geld-Zweitprüfung zu RE0090: warum die Anzahlungsrechnung nicht auf eine Zahlung lautet
      // (oder gekappt wurde) — der Betreiber muss es beim Erstellen sehen, nicht erst in LexOffice.
      if (neu?.hinweis) await bestaetigen({ titel: "Hinweis zur Rechnung", text: neu.hinweis, bestaetigen: "Verstanden", abbrechen: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /invoice_not_configured|503/i.test(msg)
          ? "Die Rechnungsstellung ist für diesen Betrieb nicht eingerichtet: Es ist kein eigener LexOffice-API-Schlüssel hinterlegt. Diesen unter Einstellungen → Module → LexOffice eintragen."
          : /no_active_offer_options/i.test(msg)
            ? "Es gibt keine aktive Angebotsvariante zum Abrechnen."
            : /no_deposit_terms/i.test(msg)
              ? "Keine Anzahlung in den Zahlungsbedingungen hinterlegt (Einstellungen → Zahlungen)."
              : /no_deposit_invoice/i.test(msg)
                ? "Für die Schlussrechnung zuerst die Anzahlungsrechnung erstellen."
                : /nothing_to_bill/i.test(msg)
                  ? "Nach Anzahlungsabzug ist kein Restbetrag offen."
                  : msg,
      );
    } finally {
      setInvBusy(false);
    }
  }

  async function sendInvoice(invoiceId: string, personalNote?: string) {
    if (!id) return;
    setInvBusy(true);
    setActionError(null);
    try {
      const note = personalNote?.trim();
      await apiMutate(`/api/invoices/${invoiceId}/send`, "POST", note ? { personalNote: note } : {});
      setInvSendDialogFor(null);
      setInvPersonalNote("");
      invoicesQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /mail_not_configured/i.test(msg)
          ? "E-Mail-Versand ist nicht konfiguriert – die Rechnung wurde NICHT zugestellt."
          : /no_recipient/i.test(msg)
            ? "Der Kunde hat keine E-Mail-Adresse – die Rechnung wurde NICHT zugestellt."
            : /send_failed/i.test(msg)
              ? "E-Mail-Versand fehlgeschlagen – die Rechnung wurde NICHT zugestellt. Bitte erneut senden."
              : msg,
      );
      invoicesQ.reload();
    } finally {
      setInvBusy(false);
    }
  }

  // Fall Ribeiro (24.09.2026): Ausweg aus „Beleg-Status ungeklärt" — nur nach eigener Nachschau in LexOffice.
  async function releaseUncertainInvoice(invoiceId: string) {
    if (!id) return;
    if (
      !(await bestaetigen({
        titel: "Haben Sie in LexOffice nachgesehen und dort KEINEN Beleg zu dieser Rechnung gefunden?",
        text: "Nur dann freigeben: danach kann die Rechnung neu erstellt werden. Existiert der Beleg doch, entstünde eine doppelte Rechnung.",
        bestaetigen: "Freigeben",
      }))
    )
      return;
    setInvBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/invoices/${invoiceId}/ungewiss-freigeben`, "POST", { inLexofficeNichtVorhanden: true });
      invoicesQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setInvBusy(false);
    }
  }

  async function cancelInvoice(invoiceId: string) {
    if (!id) return;
    if (
      !(await bestaetigen({ titel: "Rechnung wirklich stornieren?", text: "Es wird eine Gutschrift erzeugt.", bestaetigen: "Stornieren", gefahr: true }))
    )
      return;
    setInvBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/invoices/${invoiceId}/cancel`, "POST", {});
      invoicesQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setInvBusy(false);
    }
  }

  // 13.10.2026: der Plan fordert weniger als die Angebotssumme → Restzahlung über den Fehlbetrag
  // anlegen. Nur auf Klick; Fälligkeit leitet der Server ab (Restzahlungsregel, sonst Eventdatum).
  // Ein POST legt nur die Zeile an — eine Rechnung entsteht erst beim Statuswechsel auf bezahlt.
  async function restzahlungAnlegen(amountCents: number) {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/payments`, "POST", { amountCents, paymentType: "final" });
      paymentsQ.reload();
    } catch (err) {
      setActionError((err as ApiError).message ?? "Restzahlung konnte nicht angelegt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function createPayment(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    const form = new FormData(e.currentTarget);
    const amountCents = parseGermanEurosToCents((form.get("amount") as string) ?? "");
    if (amountCents == null) {
      setActionError("Ungültiger Betrag – bitte als Zahl eingeben, z. B. 1.500,00.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/events/${id}/payments`, "POST", {
        amountCents,
        paymentType: form.get("paymentType"),
        dueDate: (form.get("dueDate") as string) || undefined,
      });
      setPayOpen(false);
      setZahlungBetrag("");
      setZahlungProzent("");
      paymentsQ.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function createPaymentPlan() {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await mitVarianteWahl((chosenOptionId) => apiMutate(`/api/events/${id}/payment-plan`, "POST", chosenOptionId ? { chosenOptionId } : {}));
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /no_amount/i.test(msg)
          ? "Kein Angebotsbetrag – zuerst eine Angebotsvariante wählen."
          : /no_event_date/i.test(msg)
            ? "Kein Event-Datum – ohne Datum bekäme die Restzahlung keine Fälligkeit. Erst Datum setzen, dann Zahlungsplan anlegen."
            : /plan_exists|409/i.test(msg)
              ? "Es gibt bereits Zahlungen für diesen Vorgang."
              : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  function oeffneStaffel() {
    const heute = new Date().toISOString().slice(0, 10);
    let letzte = "";
    if (event?.eventDate) {
      const d = new Date(`${event.eventDate}T00:00:00.000Z`);
      d.setUTCDate(d.getUTCDate() - 14);
      letzte = d.toISOString().slice(0, 10);
    }
    setStaffelZeilen([
      { art: "prozent", wert: "50", faellig: heute },
      { art: "prozent", wert: "50", faellig: letzte },
    ]);
    setActionError(null);
    setStaffelOpen(true);
  }

  async function createStaffel() {
    if (!id) return;
    const pruefung = pruefeStaffel(event?.amountTotalCents ?? null, staffelZeilen);
    if (!pruefung.gueltig || !pruefung.nutzlast) {
      setActionError(pruefung.meldung);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await mitVarianteWahl((chosenOptionId) => apiMutate(`/api/events/${id}/payment-plan`, "POST", { stufen: pruefung.nutzlast, ...(chosenOptionId ? { chosenOptionId } : {}) }));
      setStaffelOpen(false);
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /plan_exists|409/i.test(msg)
          ? "Es gibt bereits Zahlungen für diesen Vorgang."
          : /no_amount/i.test(msg)
            ? "Kein Angebotsbetrag – zuerst eine Angebotsvariante wählen."
            : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  const toast = useToastWennVorhanden();
  async function paymentAction(payment: Payment, action: "checkout-link" | "send-link" | "paid" | "delete") {
    if (!id) return;
    if (action === "delete" && !(await bestaetigen({ titel: "Zahlung wirklich löschen?", bestaetigen: "Löschen", gefahr: true }))) return;
    // H-30-Rotation vom Backend bereits unterstuetzt (deaktiviert den alten Link, bevor ein
    // neuer entsteht) - hier nur die Bestaetigung dafuer, wenn tatsaechlich schon ein Link
    // existiert (sonst ist es die normale Erstanlage ohne Rueckfrage).
    if (
      action === "checkout-link" &&
      payment.checkoutUrl &&
      !(await bestaetigen({
        titel: "Neuen Zahlungslink erzeugen?",
        text: "Der bisherige Link wird dabei deaktiviert und funktioniert danach nicht mehr.",
        bestaetigen: "Neuen Link erzeugen",
      }))
    )
      return;
    // Einheit D: „Bezahlt“ öffnet den Bezahlt-Dialog (Zahlweise, Datum, fehlende Rechnungsangaben,
    // Menü) statt der früheren Bestätigung. Gebucht wird dort; hier nur öffnen.
    if (action === "paid") {
      setActionError(null);
      setBezahltZahlung(payment);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const paymentId = payment.id;
      if (action === "delete") await apiMutate(`/api/payments/${paymentId}`, "DELETE");
      else {
        const antwort = await apiMutate<{ sent?: boolean; to?: string } | undefined>(`/api/payments/${paymentId}/${action}`, "POST", {});
        if (action === "send-link") toast.success(antwort?.to ? `Zahlungslink an ${antwort.to} gesendet` : "Zahlungslink gesendet");
      }
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // `toApiError` traegt den Klartext des Servers als message (Antoine 13.09.2026: exakte
      // Fehlermeldungen). Nur wenn der Server KEINEN Satz mitgab (nackter Code oder HTTP-Text),
      // wird hier uebersetzt — bis 20.09.2026 ueberschrieb diese Stelle jeden Serversatz mit
      // „… im Worker nicht konfiguriert (COMPOSIO_API_KEY)", einem Variablennamen, mit dem der
      // Betrieb nichts anfangen kann (E2E-Betreiberweg § 4 Nr. 7).
      const nurCode = /^[a-z_]+$/.test(msg) || /^service unavailable$/i.test(msg);
      setActionError(
        !nurCode
          ? msg
          : /payment_link_not_configured|mail_not_configured|service unavailable/i.test(msg)
          ? "Zahlungsannahme bzw. Mailversand ist für diesen Betrieb nicht eingerichtet (Einstellungen → Module)."
          : /no_link/i.test(msg)
            ? "Erst einen Zahlungslink erstellen."
            : /no_customer_email/i.test(msg)
              ? "Kein Kunde mit E-Mail am Vorgang."
              : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Echte Stripe-Erstattung (2026-08-30). Betrag ist vorbefuellt mit dem vollen gezahlten Betrag,
   * aber editierbar (Teil-Erstattung) - der Server prueft ihn erneut (Preisintegritaet, siehe
   * payments.ts). Bewegt echtes Geld und ist unumkehrbar -> zusaetzliche Bestaetigungs-Huerde
   * wie beim "Bezahlt markieren"-Pfad (paymentAction), hier bewusst schaerfer formuliert.
   */
  async function refundStripe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!refundTarget) return;
    const form = new FormData(e.currentTarget);
    const amountCents = parseGermanEurosToCents((form.get("amount") as string) ?? "");
    if (amountCents == null || amountCents <= 0) {
      setActionError("Ungültiger Betrag – bitte als Zahl eingeben, z. B. 250,00.");
      return;
    }
    // Punkt 93: Deckel ist der noch nicht erstattete REST (Migration 484) — der Server prueft erneut.
    const restCents = nettoBezahltCents(refundTarget);
    if (amountCents > restCents) {
      setActionError(`Der Erstattungsbetrag darf ${money(restCents)} (der noch nicht erstattete Rest) nicht übersteigen.`);
      return;
    }
    if (refundGrund.trim().length < 3) {
      setActionError("Bitte eine Begründung angeben (mindestens 3 Zeichen) – sie steht danach im Verlauf.");
      return;
    }
    // Punkt 93: schuldet der Gast danach noch etwas, erst ausdruecklich bestaetigen lassen.
    const kzErst = event ? kennzahlenFuerVorgang(paymentsQ.data ?? [], event) : null;
    const schuldCents = schuldNachErstattungCents(kzErst?.paidCents ?? 0, kzErst?.sollCents ?? null, amountCents);
    if (schuldCents > 0 && !(schuldRueckfrage?.amountCents === amountCents && schuldBestaetigt)) {
      if (schuldRueckfrage?.amountCents !== amountCents) setSchuldBestaetigt(false);
      setSchuldRueckfrage({ amountCents, schuldCents });
      return;
    }
    if (
      !(await bestaetigen({
        titel: `Wirklich ${money(amountCents)} über Stripe an den Kunden zurückzahlen?`,
        text: "Das bewegt echtes Geld und kann NICHT rückgängig gemacht werden.",
        bestaetigen: "Zurückzahlen",
        gefahr: true,
      }))
    )
      return;
    setBusy(true);
    setActionError(null);
    try {
      await apiMutate(`/api/payments/${refundTarget.id}/refund`, "POST", { mode: "stripe", amountCents, grund: refundGrund.trim(), vorgangId: refundVorgangId });
      setRefundTarget(null);
      setRefundGrund("");
      setSchuldRueckfrage(null);
      setSchuldBestaetigt(false);
      paymentsQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(
        /amount_exceeds_paid/i.test(msg)
          ? "Der Erstattungsbetrag übersteigt den noch nicht erstatteten Rest – bitte Eingabe prüfen."
          : /no_provider_payment/i.test(msg)
            ? "Diese Zahlung lief nicht über Stripe – dafür gibt es hier keine Erstattung."
            : /refund_not_configured|503/i.test(msg)
              ? "Stripe ist im Worker nicht konfiguriert (COMPOSIO_API_KEY)."
              : /not_refundable|409/i.test(msg)
                ? "Diese Zahlung ist nicht (mehr) erstattungsfähig – Status hat sich zwischenzeitlich geändert."
                : /refund_failed|502/i.test(msg)
                  ? "Stripe hat die Erstattung abgelehnt. Bitte Betrag/Status prüfen."
                  : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  if (!id) return <ErrorNote message="Kein Event angegeben." />;
  if (eventQ.loading) return <LoadingRows rows={6} />;
  if (eventQ.error) return <ErrorNote message={eventQ.error} />;
  if (!event) return <EmptyState icon="event_busy" title="Event nicht gefunden" />;

  const payments = paymentsQ.data ?? [];
  const invoices = invoicesQ.data ?? [];
  // WP2 (08.09.2026): Bezahlt/Offen inkl. "vollständig bezahlt"-Sonderregel (M1, Bughunt
  // 2026-07-11) kam vorher hier UND in `event-detail.tsx` fast identisch vor — jetzt eine Stelle.
  // Abrechnung Stufe 3: mit abgeschlossener Abrechnung misst „Bezahlt/Offen“ an ihrem Betrag.
  const { paidCents, openCents, vollstaendigBezahlt } = kennzahlenFuerVorgang(payments, event);
  // Abrechnung Stufe 3: nach einer abgeschlossenen Abrechnung ist der Zahlungsplan kein Maßstab mehr —
  // „Plan fordert X von Angebotssumme Y“ wäre dann falsch. Maßgeblich ist der Rest der Abrechnung.
  const deckungshinweis = event.abrechnungDeckung ? null : deckungsSatz(paymentsQ.zusatz?.deckung);
  const fehlbetrag = event.abrechnungDeckung ? null : fehlbetragCents(paymentsQ.zusatz?.deckung);
  const hasDeposit = invoices.some((i) => i.invoiceType === "deposit" && i.status !== "voided");
  const hasFinalOrFull = invoices.some((i) => (i.invoiceType === "final" || i.invoiceType === "invoice") && i.status !== "voided");

  return (
    <>
      {bestaetigungsDialog}
      {varianteDialog}
      <BezahltDialog
        open={bezahltZahlung != null}
        zahlung={bezahltZahlung}
        kundeName={customerQ.data?.name ?? null}
        onClose={() => setBezahltZahlung(null)}
        onFertig={() => {
          setBezahltZahlung(null);
          paymentsQ.reload();
        }}
      />
      <RechnungSendenDialog
        open={rechnungZahlung != null}
        zahlung={rechnungZahlung}
        kundeName={customerQ.data?.name ?? null}
        grund={rechnungZahlung ? rechnungOffenGrund(rechnungZahlung, []) : null}
        vorStichtag={rechnungZahlung ? rechnungZahlung.rechnung?.nachStichtag !== true : false}
        onClose={() => setRechnungZahlung(null)}
        onFertig={() => {
          setRechnungZahlung(null);
          paymentsQ.reload();
          invoicesQ.reload();
        }}
      />
      <PageHeader
        title="Abrechnung"
        // Punkt 115 (G3): „<Vorgang> › Abrechnung“ statt des frueheren Knopfs „← Zurück zum Auftrag“.
        zurueck={{ label: anlassAnzeige(event.occasion) ?? "Vorgang", to: vorgangPfad(event.id) }}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {EVENT_STATUS[event.status] && <StatusPill meta={EVENT_STATUS[event.status]} />}
            {customerQ.data && <span>{customerQ.data.name}</span>}
            {event.eventDate && <span className="text-[#C9BFAC]">·</span>}
            {event.eventDate && <span>{formatDate(event.eventDate)}</span>}
            {event.guestCount != null && <span className="text-[#C9BFAC]">·</span>}
            {event.guestCount != null && <span>{event.guestCount} Gäste</span>}
          </span>
        }
      />

      {/* Reiter unter dem PageHeader (freigegebenes Mockup 16.09.2026). `TabBar` stellt nur die
          Kopfzeile, den Inhalt schaltet die Seite darunter selbst — dasselbe Muster wie
          `pages/admin/tenant-detail.tsx`. */}
      <div className="mt-4">
        <TabBar
          tabs={[
            { value: "uebersicht", label: "Übersicht" },
            { value: "abrechnen", label: "Abrechnen" },
            { value: "dokumente", label: "Dokumente" },
          ]}
          value={tab}
          onValueChange={(v) => setTab(v === "dokumente" || v === "abrechnen" ? v : "uebersicht")}
        />
      </div>

      {actionError && <ErrorNote message={actionError} />}

      {tab === "abrechnen" && (
        <div className="mt-4">
          <AbrechnenReiter eventId={id} onGeaendert={() => { paymentsQ.reload(); invoicesQ.reload(); }} />
        </div>
      )}

      {tab === "dokumente" && (
        <div className="mt-4">
          <AbrechnungDokumente eventId={id} event={event} invoices={invoices} costAcceptance={caQ.data} />
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────────────────────────
          Reiter „Übersicht": der bisherige Seiteninhalt, 1:1 und unveraendert hierher verschoben
          (Pipeline-Karte, Angebot-, Kostenuebernahme-, Rechnungs- und Zahlungs-Zeile samt aller
          Aktionen). Kein Aktionsweg, kein Bestaetigungsdialog, keine Fehlerbehandlung wurde dabei
          angefasst — nur die Verschachtelung um eine Ebene.
          ───────────────────────────────────────────────────────────────────────────────────── */}
      {tab === "uebersicht" && (
      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-title-md text-title-md font-semibold text-on-background dark:text-inverse-on-surface">Kostenübernahme → Rechnung → Zahlungseingang</h3>
            <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
              B2B
            </span>
          </div>
          {/* Punkt 115 (Paket 1): die Zahlungen DIESES Vorgangs, nicht die ungefilterte Betriebsliste. */}
          <Link
            to={zahlungenPfad({ vorgang: event.id, von: abrechnungPfad(event.id), vonName: "Abrechnung" })}
            className="font-label-md text-label-md text-primary hover:underline"
          >
            Alle Zahlungen dieses Vorgangs
          </Link>
        </div>

        <div className="divide-y divide-outline-variant/30 dark:divide-outline/20">
          {/* Zeile 0: Angebot (WARTESCHLANGE Punkt 6, Teil b, 14.09.2026, Antoine: "was hat der
              Gast bereits zugeschickt bekommen") — rein lesend, keine neue Aktion/Mutation.
              `event.publicToken`/`event.offerSentAt` sind auf EventRow bereits vorhanden und
              werden bereits ueber `eventQ` geladen, kein neuer API-Aufruf noetig. Bewusst KEIN
              PDF-Link je Angebotsvariante hier (würde die Angebotsoptionen-Liste laden, die
              diese Seite heute nicht laedt) — das Angebot-PDF bleibt auf event-detail.tsx, s.
              Kopfkommentar dieser Datei. Chronologisch VOR der Kostenübernahme-Zeile. */}
          <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
            <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
              <Icon name="description" className="text-[18px]" />
            </div>
            <div className="min-w-0">
              <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Angebot</p>
              {event.offerSentAt ? (
                <>
                  <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                    Angebot gesendet am {formatDate(event.offerSentAt)}
                  </p>
                  {/*
                    ZWEI GRÜNDE, WARUM HIER DIE BETREIBER-ANSICHT STEHT UND NICHT DIE GAST-URL:

                    1. Antoine am 21.09.2026: „Der Betreiber soll es auch als PDF sehen können."
                       Auf dieser Seite sitzt der Knopf „Als PDF herunterladen / Drucken" — es ist
                       dasselbe Dokument, das der Gast sieht, nur ohne zweite Wahrheit daneben.
                    2. DER ALTE LINK VERFÄLSCHTE DIE EIGENE AUSKUNFT. Er führte auf `/angebot/:token`,
                       also auf die Gast-Seite — und die zählt jeden Aufruf ohne Anmeldung als
                       Gast-Aufruf. Wer im Panel nachsah, erhöhte damit „N× geöffnet" und machte die
                       Auskunft kaputt, an der er gerade ablesen wollte, ob der Gast schon
                       hineingeschaut hat. Die Betreiber-Ansicht lädt angemeldet und zählt nicht.
                  */}
                  <a
                    href={angebotsseitePfad(event.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1.5 font-label-md text-[11.5px] text-primary hover:underline"
                  >
                    <Icon name="open_in_new" className="text-[15px]" /> Angebot ansehen · als PDF sichern
                  </a>
                </>
              ) : (
                <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Noch nicht gesendet.</p>
              )}
            </div>
          </div>

          {/* Zeile 1: Kostenübernahme */}
          <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
            <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
              <Icon name="draw" className="text-[18px]" />
            </div>
            {/* A3 (13.09.2026): Darstellung extrahiert nach `ui/auftrag/KostenuebernahmeDetail.tsx`
                — dieselbe Komponente zeigt die Vorgangsseite im Kachel-Drawer. Die Aktionen
                rechts daneben („Testvertrag"/„Anfordern"/„Widerrufen") bleiben hier: sie gehoeren
                zur Abrechnungs-Unterseite, nicht zur Darstellung. */}
            <div className="min-w-0">
              <KostenuebernahmeDetail eventId={id} costAcceptance={caQ.data} />
            </div>
            {!caQ.data && (
              <div className="col-span-2 flex flex-wrap items-center gap-3 sm:col-span-1 sm:flex-col sm:items-end sm:gap-2">
                <label className="flex items-center gap-1.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                  <Checkbox checked={caTest} onChange={(e) => setCaTest(e.target.checked)} /> Testvertrag
                </label>
                <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={caBusy} onClick={() => setCaDialogOpen(true)}>
                  {caBusy ? "Wird erstellt …" : "Anfordern"}
                </Button>
              </div>
            )}
            {/* WARTESCHLANGE (Antoine, 15./16.09.2026, HAT PRIO): "Wenn sich im Angebot etwas
                ändert, muss der Betreiber eine neue Kostenübernahme schicken können." Sobald
                irgendeine Kostenübernahme existiert — UNABHÄNGIG vom Status (signiert, abgelehnt,
                widerrufen, zugestellt) —, muss ein Neuversand erreichbar bleiben; vorher
                verschwand der Anfordern-Weg komplett, sobald `caQ.data` nicht mehr leer war.
                Nutzt bewusst denselben Dialog/Preisvorschau-Weg (`caDialogOpen`,
                `previewCostAcceptance`, `requestCostAcceptance`) wie der Erstversand oben — keine
                zweite, abweichende Versandlogik. Backend: routes/cost-acceptance.ts supersedet den
                bestehenden Vertrag serverseitig (H-42/M-23 für 'sent', der neue Reopening-
                Sonderfall für 'signed' — apps/api/test/cost-acceptance.test.ts, describe
                "Reopening — ..."). */}
            {caQ.data && (
              <div className="col-span-2 flex flex-wrap items-center gap-3 sm:col-span-1 sm:flex-col sm:items-end sm:gap-2">
                <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={caBusy} onClick={() => setCaDialogOpen(true)}>
                  {caBusy ? "Wird erstellt …" : "Neu anfordern"}
                </Button>
              </div>
            )}
            {/* Widerruf nur im Status 'sent' — genau der Status, den die Route selbst verlangt
                (409 invalid_status sonst, cost-acceptance.ts). Eine bereits signierte
                Kostenübernahme ist verbindlich und hat hier bewusst KEINEN Widerruf-Button (dafür
                den "Neu anfordern"-Weg oben, der den zugestellten Vertrag automatisch ersetzt). */}
            {/* KÜ V2 (S2): gilt V1 und wartet V2 auf Unterschrift, zeigt die Zeile V1 — der Widerruf
                betrifft dann die offene V2 (die Route widerruft die zugestellte Zeile). */}
            {(caQ.data?.status === "sent" || caQ.data?.offeneVersion) && (
              <div className="col-span-2 flex flex-wrap items-center gap-3 sm:col-span-1 sm:flex-col sm:items-end sm:gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="!py-1.5 !text-[11.5px]"
                  disabled={caBusy}
                  onClick={() => void withdrawCostAcceptance()}
                >
                  {caBusy ? "Wird widerrufen …" : caQ.data?.offeneVersion ? `V${caQ.data.offeneVersion.versionNo ?? "?"} widerrufen` : "Widerrufen"}
                </Button>
              </div>
            )}
          </div>

          {/* Zeile 2: Rechnungen */}
          <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
            <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
              <Icon name="receipt_long" className="text-[18px]" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Rechnung</p>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-label-md text-[11px] ${invoices.length > 0 ? invMeta(invoices[0]!).cls : NOT_REQUESTED.cls}`}>
                  {invoices.length > 0 ? invMeta(invoices[0]!).label : "Keine Rechnung"}
                </span>
                <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">LexOffice</span>
              </div>
              <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                Aus der gewählten Angebotsvariante, als PDF versendbar.
              </p>
              {invoices.length > 0 && (
                <ul className="mt-2 space-y-2">
                  {invoices.map((inv) => {
                    const zustand = invMeta(inv);
                    return (
                      <li key={inv.id} className="rounded-lg border border-outline-variant/30 p-2.5 dark:border-outline/30">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                            {INVOICE_TYPE_LABEL[inv.invoiceType]}
                          </span>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-label-md text-[10.5px] ${zustand.cls}`}>
                            {zustand.label}
                          </span>
                          <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                            {inv.invoiceNumber ?? "—"}
                            {inv.amountGrossCents != null ? ` · ${money(inv.amountGrossCents)}` : ""}
                          </span>
                        </div>
                        {zustand.hinweis && (
                          <p className="mt-1 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">{zustand.hinweis}</p>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-3">
                          {inv.pdfUrl && (
                            <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline">
                              <Icon name="picture_as_pdf" className="text-[15px]" /> PDF
                            </a>
                          )}
                          {zustand.sendbar && (
                            <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline disabled:opacity-50" disabled={invBusy} onClick={() => setInvSendDialogFor(inv.id)}>
                              <Icon name="send" className="text-[15px]" /> {inv.sentAt ? "Erneut senden" : "An Kunden senden"}
                            </button>
                          )}
                          {zustand.freigebbar && (
                            <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-primary hover:underline disabled:opacity-50" disabled={invBusy} onClick={() => void releaseUncertainInvoice(inv.id)}>
                              <Icon name="lock_open" className="text-[15px]" /> In LexOffice nicht vorhanden – freigeben
                            </button>
                          )}
                          {zustand.stornierbar && (
                            <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11.5px] text-error hover:underline disabled:opacity-50" disabled={invBusy} onClick={() => void cancelInvoice(inv.id)}>
                              <Icon name="undo" className="text-[15px]" /> Stornieren
                            </button>
                          )}
                          {inv.sentAt && (
                            <span className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">gesendet {formatDate(inv.sentAt)}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col sm:items-end">
              {lexofficeKeyFehlt && !hasFinalOrFull && (
                <p data-testid="rechnung-ohne-lexoffice" className="flex items-start gap-1.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim sm:max-w-[220px] sm:text-right">
                  <Icon name="info" className="mt-px text-[15px]" />
                  <span>Rechnungen ab Anbindung von LexOffice — Einstellungen → Module</span>
                </p>
              )}
              {!lexofficeKeyFehlt && !hasFinalOrFull && (
                <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("deposit")}>
                  Anzahlung
                </Button>
              )}
              {!lexofficeKeyFehlt && hasDeposit && !hasFinalOrFull && (
                <Button type="button" variant="primary" className="!py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("final")}>
                  Schlussrechnung
                </Button>
              )}
              {!lexofficeKeyFehlt && !hasDeposit && !hasFinalOrFull && (
                <Button type="button" variant="primary" className="!py-1.5 !text-[11.5px]" disabled={invBusy} onClick={() => void createInvoice("invoice")}>
                  {invBusy ? "Wird erstellt …" : "Vollrechnung"}
                </Button>
              )}
            </div>
          </div>

          {/* Zeile 3: Zahlungen */}
          <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 py-3.5 sm:grid-cols-[34px_minmax(0,1fr)_auto]">
            <div className="grid h-[34px] w-[34px] place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
              <Icon name="credit_card" className="text-[18px]" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Zahlungen</p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-[#6F675A]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#6F675A]" />
                  {vollstaendigBezahlt ? "Vollständig bezahlt" : `Offen · ${money(openCents)}`}
                </span>
              </div>
              <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                {payments.length === 0
                  ? "Noch keine Zahlungen erfasst."
                  : `${money(paidCents)}${event.amountTotalCents ? ` von ${money(event.amountTotalCents)}` : ""} eingegangen.`}
              </p>
              {deckungshinweis && (
                <div className="mt-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 dark:bg-white/[0.08]">
                  <p className="flex items-start gap-1.5 font-body-sm text-[11.5px] text-[#6F675A]">
                    <Icon name="info" className="mt-px shrink-0 text-[14px]" />
                    <span className="min-w-0 break-words">{deckungshinweis}</span>
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {fehlbetrag != null && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-label-md text-[11px] font-semibold text-primary underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                        disabled={busy}
                        onClick={() => void restzahlungAnlegen(fehlbetrag)}
                      >
                        <Icon name="add" className="text-[15px]" /> Restzahlung anlegen ({money(fehlbetrag)})
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-label-md text-[11px] font-semibold text-[#6F675A] underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                      aria-label="Plan anpassen …"
                      disabled={busy}
                      onClick={() => {
                        setActionError(null);
                        setAnpassenOpen(true);
                      }}
                    >
                      <Icon name="tune" className="text-[15px]" /> Plan anpassen …
                    </button>
                  </div>
                </div>
              )}
              {paymentsQ.loading ? (
                <LoadingRows rows={1} />
              ) : payments.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {payments.map((p) => (
                    <li key={p.id} className="rounded-lg border border-outline-variant/30 p-2.5 dark:border-outline/30">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                            {PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}
                          </p>
                          <p className="mt-0.5 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
                            {p.dueDate ? `Fällig ${formatDate(p.dueDate)}` : formatDate(p.createdAt)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <p className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">{money(p.amountCents)}</p>
                          <StatusPill meta={PAYMENT_STATUS[p.status]} />
                        </div>
                      </div>
                      {p.status !== "succeeded" && p.status !== "refunded" && (
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-outline-variant/30 pt-2 dark:border-outline/20">
                          {p.checkoutUrl ? (
                            <>
                              <a href={p.checkoutUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline">
                                <Icon name="link" className="text-[15px]" /> Link
                              </a>
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "send-link")}>
                                <Icon name="send" className="text-[15px]" /> Senden
                              </button>
                              <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-tertiary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "checkout-link")}>
                                <Icon name="autorenew" className="text-[15px]" /> Link erneuern
                              </button>
                            </>
                          ) : (
                            <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "checkout-link")}>
                              <Icon name="link" className="text-[15px]" /> Zahlungslink
                            </button>
                          )}
                          <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-tertiary hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "paid")}>
                            <Icon name="check_circle" className="text-[15px]" /> Bezahlt
                          </button>
                          <button type="button" className="inline-flex items-center gap-1 font-label-md text-[11px] text-error hover:underline disabled:opacity-50" disabled={busy} onClick={() => void paymentAction(p, "delete")}>
                            <Icon name="delete" className="text-[15px]" /> Löschen
                          </button>
                        </div>
                      )}
                      {rechnungGesendetText(p) && (
                        <p className="mt-2 border-t border-outline-variant/30 pt-2 font-label-md text-[11px] text-secondary dark:border-outline/20 dark:text-secondary-fixed-dim" data-testid={`rechnung-gesendet-${p.id}`}>
                          {rechnungGesendetText(p)}
                        </p>
                      )}
                      {rechnungSendbar(p) && !(p.rechnung?.modus === "neu" && lexofficeKeyFehlt) && (
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-outline-variant/30 pt-2 dark:border-outline/20">
                          <Button
                            type="button"
                            variant="secondary"
                            className="!py-1 !text-[11px]"
                            disabled={busy}
                            data-testid={`rechnung-senden-${p.id}`}
                            onClick={() => {
                              setActionError(null);
                              setRechnungZahlung(p);
                            }}
                          >
                            <Icon name="forward_to_inbox" className="text-[15px]" /> Rechnung senden
                          </Button>
                        </div>
                      )}
                      {p.status === "succeeded" && p.stripePaymentIntentId && (
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-outline-variant/30 pt-2 dark:border-outline/20">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 font-label-md text-[11px] text-error hover:underline disabled:opacity-50"
                            disabled={busy}
                            onClick={() => {
                              setActionError(null);
                              setRefundGrund("");
                              setSchuldRueckfrage(null);
                              setSchuldBestaetigt(false);
                              setRefundVorbelegtCents(null);
                              setRefundVorgangId(crypto.randomUUID());
                              setRefundTarget(p);
                            }}
                          >
                            <Icon name="undo" className="text-[15px]" /> Stripe-Erstattung …
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col sm:items-end">
              {payments.length === 0 && (
                <>
                  <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={busy} onClick={() => void createPaymentPlan()}>
                    Zahlungsplan
                  </Button>
                  <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={busy} onClick={oeffneStaffel}>
                    Staffel …
                  </Button>
                </>
              )}
              <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" onClick={() => setPayOpen(true)}>
                <Icon name="add" className="text-[15px]" /> Zahlung
              </Button>
            </div>
          </div>
        </div>
      </Card>
      )}

      <Drawer
        open={staffelOpen}
        onClose={() => setStaffelOpen(false)}
        title="Zahlungsplan als Staffel"
        subtitle={event.amountTotalCents ? `Angebotssumme ${money(event.amountTotalCents)}` : "Noch keine Angebotssumme – zuerst eine Variante wählen."}
      >
        {(() => {
          const pruefung = pruefeStaffel(event.amountTotalCents, staffelZeilen);
          const setzeZeile = (i: number, teil: Partial<StaffelEingabe>) =>
            setStaffelZeilen((alt) => alt.map((z, k) => (k === i ? { ...z, ...teil } : z)));
          return (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void createStaffel();
              }}
            >
              <ul className="space-y-3">
                {staffelZeilen.map((zeile, i) => (
                  <li key={i} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Stufe {i + 1}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                          {pruefung.betraegeCents[i] == null ? "–" : money(pruefung.betraegeCents[i])}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          className="!px-2 !py-1 text-error disabled:opacity-40"
                          aria-label={`Stufe ${i + 1} entfernen`}
                          disabled={staffelZeilen.length <= 1}
                          onClick={() => setStaffelZeilen((alt) => alt.filter((_, k) => k !== i))}
                        >
                          <Icon name="delete" className="text-[16px]" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[9rem,1fr,11rem]">
                      <Select
                        ariaLabel={`Stufe ${i + 1}: Art`}
                        value={zeile.art}
                        onValueChange={(v) => setzeZeile(i, { art: v === "betrag" ? "betrag" : "prozent" })}
                        options={[
                          { value: "prozent", label: "Prozent" },
                          { value: "betrag", label: "Betrag (€)" },
                        ]}
                      />
                      <Input
                        inputMode="decimal"
                        aria-label={zeile.art === "prozent" ? `Stufe ${i + 1}: Prozentsatz` : `Stufe ${i + 1}: Betrag in Euro`}
                        placeholder={zeile.art === "prozent" ? "30" : "1.500,00"}
                        value={zeile.wert}
                        onChange={(e) => setzeZeile(i, { wert: e.target.value })}
                      />
                      <DateInput
                        aria-label={`Stufe ${i + 1}: Fällig am`}
                        value={zeile.faellig}
                        onValueChange={(v) => setzeZeile(i, { faellig: v })}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                variant="secondary"
                className="!py-1.5 !text-[11.5px] disabled:opacity-40"
                disabled={staffelZeilen.length >= 24}
                onClick={() => setStaffelZeilen((alt) => [...alt, { art: "prozent", wert: "", faellig: "" }])}
              >
                <Icon name="add" className="text-[15px]" /> Stufe hinzufügen
              </Button>

              <div className="rounded-lg bg-surface-container-high px-3 py-2 dark:bg-surface-variant/20">
                <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Stufen zusammen: {money(pruefung.summeCents)}
                  {event.amountTotalCents ? ` von ${money(event.amountTotalCents)}` : ""}
                </p>
                <p role="status" className="mt-0.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                  {pruefung.meldung}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={() => setStaffelOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" variant="primary" disabled={busy || !pruefung.gueltig}>
                  {busy ? "Legt an …" : "Staffel anlegen"}
                </Button>
              </div>
            </form>
          );
        })()}
      </Drawer>

      <ZahlungsplanAnpassenDrawer
        open={anpassenOpen}
        onClose={() => setAnpassenOpen(false)}
        eventId={event.id}
        amountTotalCents={event.amountTotalCents}
        payments={payments}
        onAngepasst={() => paymentsQ.reload()}
      />

      <Drawer open={payOpen} onClose={() => setPayOpen(false)} title="Zahlung anlegen">
        <form className="space-y-4" onSubmit={(e) => void createPayment(e)}>
          {/*
            PUNKT 89 (Antoine, 22.09.2026): „Hier wäre es gut, wenn ich immer den Betrag sehe um
            den es in diesem Auftrag geht (also die 100%) und bei einer Zahlung auch einen
            Prozentsatz eingeben kann."

            DREI ENTSCHEIDUNGEN, die hier zusammenkommen:

            1. DIE AUFTRAGSSUMME WIRD ANGEZEIGT, NICHT EINGEGEBEN. Sie steht am Vorgang und ist
               die Bezugsgröße („die 100 %"). `event.amountTotalCents` ist ohnehin geladen — es
               kommt keine Abfrage dazu.
            2. DER PROZENTSATZ IST EIN ZWEITER WEG ZUM SELBEN FELD, kein zusätzliches Datum.
               Gespeichert wird weiterhin NUR der Betrag; zwei gespeicherte Wahrheiten (Betrag
               UND Prozent) liefen auseinander, sobald sich die Auftragssumme ändert.
            3. OHNE AUFTRAGSSUMME KEIN PROZENTSATZ — und das Feld sagt warum. Ein Prozentsatz
               ohne Bezugsgröße wäre eine Rechnung ohne Grundlage. (Dieselbe Haltung wie bei der
               Altsystem-Messung: eine Anfrage ohne Angebot HAT keine Summe.)
          */}
          {event.amountTotalCents ? (
            <div className="rounded-[8px] bg-surface-container px-3 py-2 dark:bg-surface-container-high">
              <p className="font-body-sm text-[12px] text-on-surface-variant">Auftragssumme (100 %)</p>
              <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                {money(event.amountTotalCents)}
              </p>
            </div>
          ) : (
            <div className="rounded-[8px] bg-surface-container px-3 py-2 dark:bg-surface-container-high">
              <p className="font-body-sm text-[12px] text-on-surface-variant">
                Noch keine Auftragssumme — deshalb ist hier keine Prozent-Eingabe möglich. Zuerst
                eine Angebotsvariante wählen.
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <label className="block flex-1">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Betrag (€) *
              </span>
              <Input
                name="amount"
                required
                inputMode="decimal"
                placeholder="1.500,00"
                value={zahlungBetrag}
                onChange={(e) => {
                  const wert = e.target.value;
                  setZahlungBetrag(wert);
                  // Der Prozentsatz folgt dem Betrag — aber nur, wenn es eine Bezugsgröße gibt.
                  const cents = parseGermanEurosToCents(wert);
                  if (event.amountTotalCents && cents != null && cents > 0) {
                    setZahlungProzent((((cents / event.amountTotalCents) * 100).toFixed(2)).replace(/\.?0+$/, "").replace(".", ","));
                  } else if (!wert) setZahlungProzent("");
                }}
              />
            </label>
            {event.amountTotalCents ? (
              <label className="block w-[110px]">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  oder %
                </span>
                <Input
                  inputMode="decimal"
                  placeholder="30"
                  value={zahlungProzent}
                  onChange={(e) => {
                    const wert = e.target.value;
                    setZahlungProzent(wert);
                    const prozent = Number(wert.replace(",", "."));
                    if (Number.isFinite(prozent) && prozent > 0) {
                      // Kaufmännisch runden — der Betrag ist das, was gespeichert wird.
                      const cents = Math.round((event.amountTotalCents! * prozent) / 100);
                      setZahlungBetrag((cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                    } else if (!wert) setZahlungBetrag("");
                  }}
                />
              </label>
            ) : null}
          </div>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Typ
            </span>
            {/* Design E6: `Select` mit `name` — Radix legt im Formular ein verstecktes natives
                Feld an, `new FormData(...).get("paymentType")` liest den Wert unveraendert. */}
            <Select
              name="paymentType"
              defaultValue="deposit"
              options={Object.entries(PAYMENT_TYPE_LABEL).map(([k, v]) => ({ value: k, label: v }))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Fällig am
            </span>
            <DateInput name="dueDate" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setPayOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "Speichert …" : "Zahlung anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>

      <Drawer open={refundTarget !== null} onClose={() => setRefundTarget(null)} title="Stripe-Erstattung">
        {refundTarget &&
          (() => {
            // F3 (docs/KONZEPT-ABSAGE-STORNO-LEBENSZYKLUS.md, Antoine freigegebenes Mockup
            // "Storno-Hinweis & Retentions-Vorschlag"): der Vorschlag bezieht sich auf GENAU
            // diese Zahlung (`refundTarget.amountCents`), nicht auf die Summe aller Zahlungen des
            // Vorgangs — der Drawer erstattet immer nur eine einzelne Zahlung.
            // Nur relevant, wenn die Veranstaltung tatsaechlich abgesagt ist UND eine Staffel
            // gepflegt ist — ohne Staffel bleibt alles exakt wie bisher (dokumentierter
            // Fallback-Fall aus dem Konzept-Doc).
            const tiers = settingsQ.data?.cancellationTiers ?? null;
            const tage = event ? tageBisEvent(event.eventDate, event.dateEnd ?? null) : null;
            const keepPercent =
              event?.status === "cancelled" && tiers && tiers.length > 0 && tage != null
                ? ermittleKeepPercent(tiers, tage)
                : null;
            // Punkt 93 (Migration 484): bereits teil-erstattetes Geld ist schon zurueck — der Vorschlag
            // zieht es ab, und Deckel wie Vorbelegung sind der noch nicht erstattete REST.
            const bereitsErstattetCents = refundTarget.refundedAmountCents ?? 0;
            const restCents = nettoBezahltCents(refundTarget);
            const vorschlagCents =
              keepPercent != null
                ? Math.max(0, refundTarget.amountCents - berechneEinbehalt(refundTarget.amountCents, keepPercent) - bereitsErstattetCents)
                : null;
            const zeigeVorschlag = keepPercent != null && vorschlagCents != null && tage != null;
            const defaultCents = refundVorbelegtCents ?? (zeigeVorschlag ? vorschlagCents! : restCents);

            return (
              <form key={refundTarget.id} className="space-y-4" onSubmit={(e) => void refundStripe(e)}>
                <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                  {PAYMENT_TYPE_LABEL[refundTarget.paymentType] ?? "Zahlung"} über {money(refundTarget.amountCents)}
                  {customerQ.data ? ` (${customerQ.data.name})` : ""} — echte Rückzahlung über Stripe auf den ursprünglichen
                  Zahlungsweg.
                  {bereitsErstattetCents > 0 && (
                    <> Davon bereits {money(bereitsErstattetCents)} erstattet, erstattbar sind noch {money(restCents)}.</>
                  )}
                </p>
                {zeigeVorschlag && (
                  <div
                    className="rounded-lg border border-outline-variant/50 bg-surface-container-low p-3.5 dark:border-outline/30 dark:bg-surface-variant/10"
                    data-testid="refund-vorschlag-box"
                  >
                    <p className="flex items-center gap-1.5 font-body-sm text-[12px] font-bold text-on-background dark:text-inverse-on-surface">
                      <Icon name="calculate" className="text-[16px]" /> Vorschlag laut Stornobedingungen
                    </p>
                    <p className="mt-1.5 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                      {tage} {tage === 1 ? "Tag" : "Tage"} bis zum Termin → {keepPercent} % einbehalten
                    </p>
                    <div className="mt-2 flex items-center justify-between border-t border-outline-variant/40 pt-2 dark:border-outline/30">
                      <span className="font-body-sm text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
                        Vorgeschlagene Rückerstattung
                      </span>
                      <span className="font-body-sm text-[13px] font-bold text-on-background dark:text-inverse-on-surface">
                        {money(vorschlagCents)}
                      </span>
                    </div>
                  </div>
                )}
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Betrag (€) *
                  </span>
                  <Input
                    name="amount"
                    required
                    inputMode="decimal"
                    defaultValue={(defaultCents / 100).toFixed(2).replace(".", ",")}
                    onChange={() => {
                      // Die Schuld-Rueckfrage gilt nur fuer den Betrag, fuer den sie gestellt wurde.
                      setSchuldRueckfrage(null);
                      setSchuldBestaetigt(false);
                    }}
                  />
                  <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                    {zeigeVorschlag
                      ? "Vorschlag laut Stornostaffel ist vorausgefüllt"
                      : bereitsErstattetCents > 0
                        ? "Noch nicht erstatteter Rest ist vorausgefüllt"
                        : "Voller gezahlter Betrag ist vorausgefüllt"}{" "}
                    – für eine Teilerstattung Betrag anpassen. Server prüft erneut: nie mehr als {money(restCents)}.
                  </span>
                </label>
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Begründung *
                  </span>
                  {/* Einzeilig über die `Input`-Komponente (Design-Ratsche: kein rohes `.input`-Markup). */}
                  <Input
                    name="grund"
                    required
                    minLength={3}
                    maxLength={500}
                    value={refundGrund}
                    onChange={(e) => setRefundGrund(e.target.value)}
                    placeholder="Warum wird erstattet? Z. B. zwei Gäste weniger, Wein nicht geliefert."
                  />
                  <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                    Pflicht – steht danach im Verlauf des Vorgangs.
                  </span>
                </label>
                {schuldRueckfrage && (
                  <div
                    className="rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]"
                    data-testid="erstattung-schuld-rueckfrage"
                  >
                    <p>
                      Der Gast schuldet danach noch {money(schuldRueckfrage.schuldCents)}. Eine Erstattung ist normalerweise nur
                      bei Überzahlung vorgesehen. Trotzdem erstatten?
                    </p>
                    <label className="mt-1.5 flex items-center gap-2 font-semibold">
                      <Checkbox checked={schuldBestaetigt} onChange={(e) => setSchuldBestaetigt(e.target.checked)} />
                      Ja, trotzdem {money(schuldRueckfrage.amountCents)} erstatten
                    </label>
                  </div>
                )}
                <div className="rounded-lg bg-error-container px-3 py-2 dark:bg-error-container/40">
                  <p className="flex items-start gap-1.5 font-body-sm text-[11.5px] text-on-error-container">
                    <Icon name="warning" className="mt-px shrink-0 text-[14px]" />
                    <span>Bewegt echtes Geld über Stripe und kann nicht rückgängig gemacht werden.</span>
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setRefundTarget(null)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" variant="primary" disabled={busy || refundGrund.trim().length < 3 || (schuldRueckfrage !== null && !schuldBestaetigt)}>
                    {busy ? "Wird erstattet …" : "Jetzt erstatten"}
                  </Button>
                </div>
              </form>
            );
          })()}
      </Drawer>

      <Drawer open={caDialogOpen} onClose={() => setCaDialogOpen(false)} title={caQ.data ? "Kostenübernahme erneut anfordern" : "Kostenübernahme anfordern"}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void requestCostAcceptance(caPersonalNote, caOpenPricing, caVereinbarungen, kundentypFrage ? kundentypWahl : undefined);
          }}
        >
          {kundentypFrage && (
            <div data-testid="kundentyp-nachfrage" className="flex flex-col gap-2.5 rounded-lg border border-outline-variant bg-[#F0EAE0] p-3 dark:border-white/10 dark:bg-white/[0.08]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">Welcher Kundentyp ist das?</span>
                <AutomatischErkanntChip text={`erkannt: ${KUNDENTYP_TEXT[kundentypFrage]}`} />
              </div>
              <KundentypAuswahl wert={kundentypWahl} onWahl={setKundentypWahl} disabled={caBusy} testId="kundentyp-nachfrage-auswahl" />
              <span className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                Der Typ bestimmt die Vertragsvorlage (Privatperson oder Unternehmen) und wird beim Kunden gespeichert — er gilt dann für alle
                seine Aufträge. MAESTRO fragt nur dieses eine Mal.
              </span>
            </div>
          )}
          {/* WARTESCHLANGE (Antoine, 15./16.09.2026): Klarheit VOR dem Senden, was mit dem
              bestehenden Vertrag passiert — sonst könnte der Eindruck entstehen, beide blieben
              parallel gültig (harte Vorgabe der Einheit). Derselbe Dialog wie der Erstversand,
              dieser Absatz erscheint nur, wenn tatsächlich eine bestehende Kostenübernahme
              ersetzt wird. */}
          {caQ.data && (
            <p className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 font-body-sm text-[11.5px] text-secondary dark:border-outline/20">
              <strong className="font-medium text-on-background dark:text-inverse-on-surface">Ersetzt die bestehende Kostenübernahme: </strong>
              {kostenuebernahmeNeuanforderungHinweis(caQ.data.status)}
            </p>
          )}
          {gueltigeKue && !caTest && (
            <div data-testid="kue-anlass">
              <span className="mb-1 block font-body-sm text-[12px] font-medium text-on-surface-variant">Anlass der Änderung</span>
              <Select
                value={caAnlass}
                onValueChange={(v) => setCaAnlass(v as typeof caAnlass)}
                options={(Object.keys(AENDERUNGS_ANLASS_TEXT) as (keyof typeof AENDERUNGS_ANLASS_TEXT)[]).map((value) => ({ value, label: AENDERUNGS_ANLASS_TEXT[value] }))}
                placeholder="Wie beim Öffnen gewählt"
                ariaLabel="Anlass der Änderung"
                disabled={caBusy}
              />
              <span className="mt-1 block font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                Die neue Fassung ersetzt {versionsName(gueltigeKue)} erst mit der Unterschrift des Gastes. Leer lassen übernimmt den Anlass aus „Für Änderung öffnen“.
              </span>
            </div>
          )}
          <div>
            <TextbausteinPicker onInsert={(content) => insertAtCursor(caNoteRef.current, caPersonalNote, setCaPersonalNote, content)} />
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="font-body-sm text-[12px] font-medium text-on-surface-variant">
              Persönliche Nachricht (optional)
            </span>
            {/* Design-System-Migration P2: bewusst NICHT auf `Input` migriert — `Input` rendert
                ausschließlich ein `<input>`, kein `<textarea>`; ein Tausch hätte
                Mehrzeiligkeit/Resize gekostet (derselbe bereits etablierte Grund wie überall
                sonst im Repo). */}
            <Textarea
              ref={caNoteRef}
              value={caPersonalNote}
              onChange={(e) => setCaPersonalNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Wird als zusätzlicher Absatz in die Signatur-Einladung eingefügt."
              className="resize-y"
            />
            <Zeichenzaehler current={caPersonalNote.length} max={2000} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="font-body-sm text-[12px] font-medium text-on-surface-variant">
              Individuelle Vereinbarungen (optional)
            </span>
            <Textarea
              value={caVereinbarungen}
              onChange={(e) => setCaVereinbarungen(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Steht als eigener Punkt im Vertrag — z. B. „Aufbau ab 16 Uhr möglich&ldquo; oder „Anzahlung 30 % bis 01.11.&ldquo;."
              className="resize-y"
            />
            <span className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
              Gilt nur für diesen Vertrag. Die Stornobedingungen stehen bereits an eigener Stelle — hier gehört
              nur hinein, was zusätzlich vereinbart wurde. Leer lassen, wenn es nichts gibt.
            </span>
            <Zeichenzaehler current={caVereinbarungen.length} max={4000} />
          </label>
          <label className="flex items-start gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 dark:border-outline/20">
            <Checkbox
              className="mt-0.5"
              checked={caOpenPricing}
              onChange={(e) => setCaOpenPricing(e.target.checked)}
            />
            <span className="flex flex-col gap-0.5">
              <span className="font-body-sm text-[12.5px] font-medium text-on-background dark:text-inverse-on-surface">
                Kein fester Endbetrag (A-la-carte)
              </span>
              <span className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                Die Kosten entstehen erst vor Ort je nach Bestellung. Der Vertrag nennt dann keine Zahl, sondern
                „gemäß Bestellung vor Ort" — kein Platzhalter-Preis nötig.
              </span>
            </span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCaDialogOpen(false)}>
              Abbrechen
            </Button>
            {/* Vorschau (08.09.2026): rein additiv, "Senden" bleibt bewusst jederzeit klickbar (kein
                erzwungenes Gate). Begründung: die serverseitige Validierung (Pflichtfelder, Betrag,
                Variantenwahl) verhindert bereits einen unvollständigen Vertrag; das im Jad-El-Haber-
                Vorfall aufgetretene Layout-Problem betraf die Vorlagen-Formatierung selbst, die auch
                ein Pflicht-Vorschau-Klick nicht automatisch vermieden hätte, wohl aber sichtbar
                gemacht hätte. Ein hartes "erst Vorschau, dann Senden"-Gate würde jeden Versand um
                einen Klick verzögern, obwohl es viele unauffällige Fälle gibt (z. B. Wiederholungs-
                versand nach kleiner Korrektur), bei denen der Betreiber die Vorlage schon kennt. */}
            <Button type="button" variant="secondary" disabled={caPreviewBusy} onClick={() => void previewCostAcceptance(caOpenPricing, caVereinbarungen)}>
              {caPreviewBusy ? "Vorschau wird erstellt …" : "Vorschau ansehen"}
            </Button>
            <Button type="submit" variant="primary" disabled={caBusy}>
              {caBusy ? "Wird erstellt …" : kundentypFrage ? "Bestätigen und senden" : "Senden"}
            </Button>
          </div>
        </form>
      </Drawer>

      <Drawer open={invSendDialogFor !== null} onClose={() => setInvSendDialogFor(null)} title="Rechnung an Kunden senden">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (invSendDialogFor) void sendInvoice(invSendDialogFor, invPersonalNote);
          }}
        >
          <div>
            <TextbausteinPicker onInsert={(content) => insertAtCursor(invNoteRef.current, invPersonalNote, setInvPersonalNote, content)} />
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="font-body-sm text-[12px] font-medium text-on-surface-variant">
              Persönliche Nachricht (optional)
            </span>
            {/* Design-System-Migration P2: bewusst NICHT auf `Input` migriert — `Input` rendert
                ausschließlich ein `<input>`, kein `<textarea>`; ein Tausch hätte
                Mehrzeiligkeit/Resize gekostet (derselbe bereits etablierte Grund wie überall
                sonst im Repo). */}
            <Textarea
              ref={invNoteRef}
              value={invPersonalNote}
              onChange={(e) => setInvPersonalNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Wird als zusätzlicher Absatz in die Rechnungs-Mail eingefügt."
              className="resize-y"
            />
            <Zeichenzaehler current={invPersonalNote.length} max={2000} />
          </label>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setInvSendDialogFor(null)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={invBusy}>
              {invBusy ? "Wird gesendet …" : "Senden"}
            </Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
